import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import createFakeEvent from '../../lib/createFakeEvent';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import PopupMenu from '../PopupMenu/PopupMenu';

/**
 * `Breadcrumbs` is a component for showing the context or path of an item, and is great for navigational purposes. Each
 * parent is a link and the user can use these to navigate directly to a parent.
 *
 * An optional **Back** button can be displayed to the left of the breadcrumbs for easy access to the closest parent.
 * Long clicking the **Back** button will display a dropdown menu with all elements in the path.
 *
 * If the width of the breadcrumbs is wider than the parent container then the first breadcrumbs will disappear behind
 * the **Back** button or the leftmost edge of the component.
 *
 * The components supports highlighting one of the elements in the path with an icon if the property *highlightedType*
 * matches an object's `contentType` property. The property *highlightedIconName* will be used as icon name. Only one element
 * in the path can be highlighted at any given time.
 *
 * @property {string} className The class name of this component. Will be appended to "core-breadcrumbs".
 * @property {string} highlightedIconName The icon name to add to a breadcrumb element that matches the provided
 * *highlightedType* property. Defaults to "page".
 * @property {string} highlightedType The type that an path element's `contentType` property must match. Defaults to "page".
 * @property {function} onClick A callback that is called when the user has clicked on any of the breadcrumb links or
 * the **Back** button. The callback will be passed the path object as `event.target.value`.
 * @property {array} path An array of objects representing the path. If the object contains a property named `title` or
 * `label` this property will be used as name for the path element. If not, the component will assume that the path
 * element is a string and display that instead.
 * @property {boolean} readOnly If set to `true`, the breadcrumbs will be read only and not contain any links. No **Back**
 * button will be displayed and the path elements will not disappear the leftmost edge. Defaults to `false`.
 *
 * @see Header, Menu
 */
export default class Breadcrumbs extends PureComponent {

  static displayName = 'Breadcrumbs';

  static propTypes = {
    className: PropTypes.string,
    highlightedIconName: PropTypes.string,
    highlightedType: PropTypes.string,
    onClick: PropTypes.func,
    path: PropTypes.array,
    readOnly: PropTypes.bool,
  };

  static defaultProps = {
    path: [],
    highlightedIconName: 'page',
    highlightedType: 'page',
  };

  state = {};

  onBack = e => {
    const { onClick, path } = this.props;

    if (onClick && path.length >= 2) {
      this.props.onClick(createFakeEvent(path[path.length - 2], e));
    }
  };

  onBackMouseDown = () => {
    this.timer = setTimeout(() => {
      const rect = this.refBack.getClientRects()[0];

      this.timer = undefined;
      this.setState({
        showPopup: true,
        anchor: {
          top: rect.top + rect.height,
          left: rect.left,
        },
      });
    }, 350);
  };

  onBackMouseUp = e => {
    if (this.timer) {
      e.preventDefault();
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  };

  onClick = e => {
    e.preventDefault();
    if (this.props.onClick) {
      const index = parseInt(e.target.getAttribute('data-index'), 10);

      this.props.onClick(createFakeEvent(this.props.path[index], e));
    }
  };

  onCloseMenu = () => this.setState({ showPopup: false });

  getItem = item => item.title || item.label || (typeof item === 'string' ? item : 'Untitled');

  createCrumb = (index, item, last, highlightedIconName, highlightedType, readOnly) => {
    let crumb = this.getItem(item);
    const cls = cx({ root: index === 0 });
    const icon = highlightedType && item.contentType === highlightedType && <Icon data-index={index} name={highlightedIconName}/>;

    if (last || readOnly || !this.props.onClick) {
      crumb = <span className={cls}>{icon}{crumb}</span>;
    } else {
      crumb = (
        <a href={item.url || item.id} onClick={this.onClick} className={cls} data-index={index}>
          {icon}
          {crumb}
        </a>
      );
    }
    return (
      <span key={index} className={cx('crumb', { last })}>
        {crumb}
        {!last && <span className="separator"> &#62; </span>}
      </span>
    );
  };

  render() {
    const { className, path, highlightedIconName, highlightedType, onClick, readOnly } = this.props;
    const crumbs = path.map((item, index) => this.createCrumb(index, item, index === path.length - 1, highlightedIconName, highlightedType, readOnly));
    let back;
    let popup;

    if (onClick && !readOnly) {
      back = (
        <button className={cx('back', { active: this.state.showPopup })} onClick={this.onBack} onMouseDown={this.onBackMouseDown}
          onMouseUp={this.onBackMouseUp} disabled={path.length === 1} ref={c => { this.refBack = c; }}>
          <Icon name="arrow-left"/>
        </button>
      );
      if (this.state.showPopup) {
        let items = path.slice(0, path.length - 1);
        const defIcon = highlightedType && items.find(item => item.contentType === highlightedType) ? 'empty' : undefined;

        items = items.map((item, index) => {
          const iconName = highlightedType && item.contentType === highlightedType ? highlightedIconName : defIcon;

          return <Button key={index} data-index={index} iconName={iconName} onClick={this.onClick}>{this.getItem(item)}</Button>;
        });

        popup = (
          <PopupMenu anchor={this.state.anchor} onClose={this.onCloseMenu} arrowSize={0}>
            {items}
          </PopupMenu>
        );
      }
    }
    return (
      <span className={cx('core-breadcrumbs', className, { 'read-only': readOnly})}>
        <span className="crumbs">
          {crumbs}
        </span>
        {back}
        {popup}
      </span>
    );
  }

}
